import { useEffect, useRef } from 'react';
import type { PatternAlert } from '../api/generated/types.gen';
import type { Horizon } from '../market';
import type { Toast } from './Toasts';

const RANK: Record<PatternAlert['status'], number> = { nearing: 0, forming: 1, broke: 2 };
const HORIZON: Record<Horizon, string> = { hourly: 'Hourly', '15m': '15-min' };

/** The latest stage in words. A reading aid: it says what the shape is doing, never what to do. */
function stage(a: PatternAlert) {
  if (a.status === 'broke') return `broke ${a.direction ?? ''}${a.broke_price ? ` at ${a.broke_price}` : ''}`.trim();
  if (a.status === 'nearing') {
    const level = a.direction === 'up' ? a.break_up : a.break_down;
    return `nearing its break${level ? ` at ${level}` : ''}`;
  }
  return 'forming';
}
const breaks = (a: PatternAlert) => [a.break_up && `above ${a.break_up}`, a.break_down && `below ${a.break_down}`].filter(Boolean).join(' or ');

/** The contract an alert opens on: the one you're on if it fits, otherwise the first that does. */
export const alertHorizon = (a: PatternAlert, current: Horizon): Horizon =>
  a.windows.some(w => w.horizon === current) ? current : a.windows[0].horizon;

/** Plan 073: announces each new stage. What is already there when the app connects is history, not news. */
export function useAlertToasts(alerts: PatternAlert[] | null, notify: (text: string, tone?: Toast['tone']) => void) {
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!alerts) return;
    const keys = alerts.map(a => `${a.id}:${a.status}`);
    if (seen.current == null) { seen.current = new Set(keys); return; }
    for (const a of alerts) {
      const key = `${a.id}:${a.status}`;
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      const where = `${a.coin} ${a.interval} · ${a.name}`;
      notify(a.status === 'forming' ? `${where} forming${breaks(a) ? ` · breaks ${breaks(a)}` : ''}` : `${where} ${stage(a)}`,
        a.status === 'broke' ? (a.direction === 'up' ? 'up' : 'down') : 'info');
    }
  }, [alerts, notify]);
}

/** The most urgent stage on a candle size, for its dot on the chart's timeframe buttons. */
export function intervalStage(alerts: PatternAlert[], interval: string): string | null {
  const here = alerts.filter(a => a.interval === interval);
  return (here.find(a => a.status === 'nearing') ?? here[0])?.status ?? null;
}

/** A coin's setups, most urgent first, opened from its badge. A tap opens that candle size. */
export function AlertStrip({ alerts, now, onOpen, onClose }: { alerts: PatternAlert[]; now: number; onOpen: (a: PatternAlert) => void; onClose: () => void }) {
  if (!alerts.length) return null;
  const sorted = [...alerts].sort((a, b) => RANK[a.status] - RANK[b.status] || b.stage_at - a.stage_at);
  return <div className="alert-strip" role="group" aria-label="Patterns setting up" data-testid="alert-strip">
    {sorted.map(a => {
      const left = a.windows.map(w => `${HORIZON[w.horizon]} ${Math.max(1, Math.ceil((w.close_at - now) / 60))}m`).join(' · ');
      return <button key={a.id} className={`alert ${a.family} ${a.status}`} onClick={() => onOpen(a)} data-testid="alert" data-coin={a.coin}
        title={`${a.coin} ${a.interval} ${a.name}, ${stage(a)}${breaks(a) ? `. Breaks ${breaks(a)}` : ''}. Time left: ${left}. Not a forecast.`}>
        <b>{a.coin}</b><span className="alert-size">{a.interval}</span><span>{a.name}</span><span className="alert-stage">{stage(a)}</span><small>{left}</small>
      </button>;
    })}
    <button className="alert-close" aria-label="Hide alerts" onClick={onClose}>×</button>
  </div>;
}
